import {
  AccountsService,
  GetPricingTableResponse,
} from 'base/accounts_service';
import { delay } from 'base/helpers';
import { Banner } from 'base/ui/banner';
import { Button, ButtonSize, ButtonVariant } from 'base/ui/button';
import { ColoredIcon } from 'base/ui/colored_icon';
import { Dialog } from 'base/ui/dialog';
import { RedErrorText, useSubmit } from 'base/ui/form';
import { IconInfo, IconPause } from 'base/ui/icons';
import { Grow, Inline, Rows } from 'base/ui/layout';
import {
  TextRegularLight,
  TextSmall,
  TextSmallLight,
} from 'base/ui/typography';
import { DiscountChip } from 'blocks/manage_account/ui';
import React from 'react';
import styles from './plan_buttons.css';

export function OpenStripeBillingButton({
  accountService,
  preferInvoice = false,
  ...props
}: {
  accountService: AccountsService;
  preferInvoice?: boolean;
  label: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
}) {
  const [loading, setLoading] = React.useState(false);
  const [url, setUrl] = React.useState<string | undefined>();

  function load() {
    setLoading(true);
    let rewardfulReferralId: string | undefined = undefined;

    if (window?.Rewardful?.referral) {
      rewardfulReferralId = window.Rewardful.referral;
    }
    accountService
      .createStripeBillingSession({
        preferInvoice,
        rewardfulReferralId,
      })
      .then(({ url }) => {
        setUrl(url);
        window.open(url, '_blank');
      })
      .finally(() => setLoading(false));
  }

  if (url) {
    return <Button {...props} href={url} target="_blank" />;
  } else {
    return <Button {...props} loading={loading} onClick={load} />;
  }
}

type BannerProps = {
  variant: 'grey2' | 'grey3';
};

export function ReactivatePlanBanner({
  cancelsAt,
  resumption,
  accountsService,
  onDone = reloadPage,
  variant,
}: {
  cancelsAt: string;
  resumption: GetPricingTableResponse['resumption'];
  accountsService: AccountsService;
  onDone?: () => void;
} & BannerProps) {
  const [loading, error, submit] = useSubmit(async () => {
    await accountsService.reactivateCancellingPlan();
    // delay for the backend to receive the stripe webhook
    await delay(1000);
    onDone();
  });

  const button = (
    <Rows spacing={8} xalign="end">
      <Button label="Reactivate" loading={loading} onClick={submit} />
      <RedErrorText error={error} />
    </Rows>
  );

  return (
    <Banner
      variant={variant}
      actions={button}
      icon={resumption ? IconPause : IconInfo}
    >
      <Inline spacing={8}>
        <TextSmall>
          {resumption ? 'Pause Scheduled' : 'Plan Cancelled'}
        </TextSmall>
        <TextSmallLight>
          {resumption ? (
            <>
              Your plan will pause on{' '}
              <TextSmall tag="span">{cancelsAt}</TextSmall> and resume on{' '}
              <TextSmall tag="span">{resumption.resumeAt}</TextSmall>.
            </>
          ) : (
            <>
              Your plan will be changed to Free on{' '}
              <TextSmall tag="span">{cancelsAt}</TextSmall>.
            </>
          )}
        </TextSmallLight>
      </Inline>
    </Banner>
  );
}

export function PauseActiveBanner({
  resumption,
  accountsService,
  onDone = reloadPage,
}: {
  resumption: Exclude<GetPricingTableResponse['resumption'], undefined>;
  accountsService: AccountsService;
  onDone?: () => void;
}) {
  const [confirmingCancel, setConfirmingCancel] = React.useState(false);
  const [loadingCancel, errorCancel, cancel] = useSubmit(async () => {
    await accountsService.cancelPlanNow();
    await delay(1000);
    onDone();
  });
  const [loadingResume, errorResume, resume] = useSubmit(async () => {
    await accountsService.resumePlanNow();
    await delay(1000);
    onDone();
  });

  const button = (
    <Inline spacing={8}>
      <Button
        label="Cancel"
        onClick={() => setConfirmingCancel(true)}
        variant="tertiary"
      />
      <Button
        label="Resume now"
        loading={loadingResume}
        onClick={resume}
        variant="primary"
      />
    </Inline>
  );

  return (
    <Banner variant="primary" actions={button} icon={IconPause}>
      <Inline spacing={8}>
        <TextSmall>Plan Paused</TextSmall>
        <TextSmallLight>
          Your plan will resume on{' '}
          <TextSmall tag="span">{resumption.resumeAt}</TextSmall>.
        </TextSmallLight>
        <RedErrorText error={errorResume} />
      </Inline>

      {confirmingCancel && (
        <Dialog
          title="Cancel Paused Plan?"
          onClose={loadingCancel ? undefined : () => setConfirmingCancel(false)}
          buttons={
            <>
              <Button
                size="large"
                label="I’ll keep it"
                onClick={() => setConfirmingCancel(false)}
              />
              <Button
                size="large"
                variant="primary"
                label="Confirm cancel"
                onClick={cancel}
                loading={loadingCancel}
              />
            </>
          }
        >
          <CancelResumptionMessage resumption={resumption} />
          <RedErrorText error={errorCancel} />
        </Dialog>
      )}
    </Banner>
  );
}

function CancelResumptionMessage({
  resumption,
}: {
  resumption: Exclude<GetPricingTableResponse['resumption'], undefined>;
}) {
  const percent =
    100 * (1 - resumption.priceCents / resumption.comparisionPriceCents);
  const discountBox = (
    <div className={styles.discountBox}>
      <Inline spacing={0} yalign="center">
        <Grow>
          <TextRegularLight>
            {formatCentAmount(resumption.priceCents)}
            {' per '}
            {resumption.billingPeriod === 'monthly' ? 'month' : 'year'}
            {' normally '}
            <span className={styles.redDiscount}>
              {formatCentAmount(resumption.comparisionPriceCents)}
            </span>
          </TextRegularLight>
        </Grow>
        <DiscountChip percent={percent} />
      </Inline>
    </div>
  );
  return (
    <>
      <TextRegularLight>
        {percent > 1
          ? 'Your plan currently benefits from an great discount that may not be available again. Cancelling your plan means forfeiting this special offer.'
          : 'We really don’t want to lose you with how much we have coming up.'}
      </TextRegularLight>
      {percent > 1 && discountBox}
    </>
  );
}

function reloadPage() {
  window.location.reload();
}

export function PaymentProblemBanner({
  accountsService,
  variant,
}: {
  accountsService: AccountsService;
} & BannerProps) {
  return (
    <Banner
      variant={variant}
      icon={<ColoredIcon color="colorRed" icon={IconInfo} />}
      actions={
        <OpenStripeBillingButton
          accountService={accountsService}
          preferInvoice={true}
          label="Fix Payment"
          variant="primary"
        />
      }
    >
      <Inline spacing={8}>
        <TextSmall>Payment Problem</TextSmall>
        <TextSmallLight>
          Update your billing details to regain access to premium features.
        </TextSmallLight>
      </Inline>
    </Banner>
  );
}

export function formatCentAmount(
  amount: number,
  opts?: { wholeDollars?: boolean },
) {
  const fractionDigits = opts?.wholeDollars || amount % 100 === 0 ? 0 : 2;

  return `$${(amount / 100).toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
}
